import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value;

    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) return;

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);

      if (section) {
        // Get codes from the new HTML (both input and pills)
        const codes = [];
        const inputInNewHtml = section.querySelector('input[name="discount"]');
        if (inputInNewHtml instanceof HTMLInputElement && inputInNewHtml.value && inputInNewHtml.hasAttribute('readonly')) {
          codes.push(inputInNewHtml.value);
        }
        const discountPills = section.querySelectorAll('.cart-discount__pill');
        for (const pill of discountPills) {
          if (pill instanceof HTMLLIElement && pill.dataset.discountCode) {
            codes.push(pill.dataset.discountCode);
          }
        }

        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          return;
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, newHtml);
      this.#reinitRecommendations();
    } catch (error) {
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
      this.#reinitRecommendations();
    } catch (error) {
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Re-initializes recommendations components whose content was emptied by morphSection.
   */
  #reinitRecommendations() {
    requestAnimationFrame(() => {
      document.querySelectorAll('cart-recommendations-component').forEach(c => {
        const container = c.querySelector('[ref="itemsContainer"]');
        if (container && container.children.length === 0) {
          const productId = c.dataset.productId;
          const collectionHandle = c.dataset.collectionHandle;
          const sectionId = c.dataset.sectionId;

          if (collectionHandle) {
            c.loadCollectionProducts(collectionHandle);
          } else if (productId) {
            c.loadRecommendations(productId, sectionId);
          }
        }
      });
    });
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];

    // Check the input field for a discount code (first code is displayed in input)
    const discountInput = this.querySelector('input[name="discount"]');
    if (discountInput instanceof HTMLInputElement && discountInput.value && discountInput.hasAttribute('readonly')) {
      discountCodes.push(discountInput.value);
    }

    // Check pills for additional discount codes
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }

  /**
   * Handles removing the first discount code (displayed in the input field).
   * @param {MouseEvent} event - The click event.
   */
  removeFirstDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (typeof this.dataset.sectionId !== 'string') return;

    const discountInput = this.querySelector('input[name="discount"]');
    if (!(discountInput instanceof HTMLInputElement) || !discountInput.value) return;

    const firstCode = discountInput.value;
    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(firstCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    // Optimistic UI: immediately clear input and hide X button
    discountInput.value = '';
    discountInput.readOnly = false;
    discountInput.placeholder = discountInput.dataset.placeholder || '';
    const clearBtn = this.querySelector('.cart-discount__input-clear');
    if (clearBtn) clearBtn.style.display = 'none';
    // Hide discount pills immediately
    const pillsList = this.querySelector('.cart-discount__codes');
    if (pillsList) pillsList.style.display = 'none';

    const abortController = this.#createAbortController();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      const sectionId = this.dataset.sectionId;

      // If removing the last discount, observe the section to keep the form
      // visible through ALL morphs (ours + any triggered by DiscountUpdateEvent)
      if (existingDiscounts.length === 0) {
        const keepFormOpen = () => {
          const comp = document.querySelector(`cart-discount-component[data-section-id="${sectionId}"]`);
          if (!comp) return;
          const toggle = comp.querySelector('.cart-discount__toggle');
          const content = comp.querySelector('.cart-discount__content');
          if (toggle && toggle.style.display !== 'none') toggle.style.display = 'none';
          if (content && content.style.display === 'none') content.style.display = 'block';
        };

        const section = document.getElementById(`shopify-section-${sectionId}`);
        if (section) {
          const observer = new MutationObserver(keepFormOpen);
          observer.observe(section, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] });
          setTimeout(() => observer.disconnect(), 3000);
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(sectionId, data.sections[sectionId]);

      this.#reinitRecommendations();

      // Focus the input after morph
      if (existingDiscounts.length === 0) {
        requestAnimationFrame(() => {
          const comp = document.querySelector(`cart-discount-component[data-section-id="${sectionId}"]`);
          if (comp) {
            const input = comp.querySelector('input[name="discount"]');
            if (input instanceof HTMLInputElement) input.focus();
          }
        });
      }
    } catch (error) {
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Toggles the visibility of the discount form.
   * @param {MouseEvent} event - The click event.
   */
  toggleForm = (event) => {
    event.preventDefault();
    const toggle = event.target.closest('.cart-discount__toggle');
    const content = this.querySelector('.cart-discount__content');

    if (content) {
      const isHidden = content.style.display === 'none';
      content.style.display = isHidden ? 'block' : 'none';

      if (toggle) {
        toggle.style.display = isHidden ? 'none' : 'block';
      }
    }
  };
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}